import { useState, useEffect, useCallback } from 'react';

export interface SystemStressLevel {
  level: 'NORMAL' | 'ALERTE' | 'CRITIQUE' | 'RUPTURE';
  cpuLoad: number;
  effects: {
    cursorGhost: boolean;
    inputLag: number;
    glitchIcons: boolean;
    windowDistortion: boolean;
    kernelAlert: boolean;
    chromaticShift: boolean;
    textInversion: boolean;
    systemFreeze: boolean;
    fanNoise: number; // 0-1
    alertBeeps: boolean;
  };
}

export const useSystemStress = (cpuLoad: number) => {
  const [stressLevel, setStressLevel] = useState<SystemStressLevel>({
    level: 'NORMAL',
    cpuLoad: 0,
    effects: {
      cursorGhost: false,
      inputLag: 0,
      glitchIcons: false,
      windowDistortion: false,
      kernelAlert: false,
      chromaticShift: false,
      textInversion: false,
      systemFreeze: false,
      fanNoise: 0,
      alertBeeps: false,
    }
  });

  const [freezeActive, setFreezeActive] = useState(false);
  const [inversionActive, setInversionActive] = useState(false);

  // Calculer le niveau de stress basé sur la charge CPU
  const calculateStressLevel = useCallback((cpu: number): SystemStressLevel => {
    if (cpu >= 98) {
      return {
        level: 'RUPTURE',
        cpuLoad: cpu,
        effects: {
          cursorGhost: true,
          inputLag: 300, // 300ms de délai
          glitchIcons: true,
          windowDistortion: true,
          kernelAlert: true,
          chromaticShift: true,
          textInversion: true,
          systemFreeze: true,
          fanNoise: 1.0,
          alertBeeps: true,
        }
      };
    } else if (cpu >= 85) {
      return {
        level: 'CRITIQUE',
        cpuLoad: cpu,
        effects: {
          cursorGhost: true,
          inputLag: 150, // 150ms de délai
          glitchIcons: true,
          windowDistortion: true,
          kernelAlert: true,
          chromaticShift: false,
          textInversion: false,
          systemFreeze: false,
          fanNoise: 0.8,
          alertBeeps: true,
        }
      };
    } else if (cpu >= 70) {
      return {
        level: 'ALERTE',
        cpuLoad: cpu,
        effects: {
          cursorGhost: true,
          inputLag: 50, // 50ms de délai
          glitchIcons: true,
          windowDistortion: false,
          kernelAlert: false,
          chromaticShift: false,
          textInversion: false,
          systemFreeze: false,
          fanNoise: 0.4,
          alertBeeps: false,
        }
      };
    } else {
      return {
        level: 'NORMAL',
        cpuLoad: cpu,
        effects: {
          cursorGhost: false,
          inputLag: 0,
          glitchIcons: false,
          windowDistortion: false,
          kernelAlert: false,
          chromaticShift: false,
          textInversion: false,
          systemFreeze: false,
          fanNoise: 0.1,
          alertBeeps: false,
        }
      };
    }
  }, []);

  // Mettre à jour le niveau de stress quand la charge CPU change
  useEffect(() => {
    const newLevel = calculateStressLevel(cpuLoad);
    setStressLevel(newLevel);
  }, [cpuLoad, calculateStressLevel]);

  // Gérer les effets de gel système (Phase Rupture)
  useEffect(() => {
    if (stressLevel.effects.systemFreeze && !freezeActive) {
      const freezeInterval = setInterval(() => {
        setFreezeActive(true);
        setTimeout(() => setFreezeActive(false), 1000); // Gel de 1 seconde
      }, 3000 + Math.random() * 2000); // Toutes les 3-5 secondes

      return () => clearInterval(freezeInterval);
    }
  }, [stressLevel.effects.systemFreeze, freezeActive]);

  // Gérer l'inversion de texte (Phase Rupture)
  useEffect(() => {
    if (stressLevel.effects.textInversion) {
      const inversionInterval = setInterval(() => {
        setInversionActive(true);
        setTimeout(() => setInversionActive(false), 200); // Inversion de 200ms
      }, 1500 + Math.random() * 1000); // Toutes les 1.5-2.5 secondes

      return () => clearInterval(inversionInterval);
    }
  }, [stressLevel.effects.textInversion]);

  // Jouer les sons d'alerte
  useEffect(() => {
    if (stressLevel.effects.alertBeeps) {
      const beepInterval = setInterval(() => {
        // Créer un bip système strident
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.setValueAtTime(800, audioContext.currentTime); // Fréquence aiguë
        gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);
        
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.1);
      }, 500); // Bip toutes les 500ms

      return () => clearInterval(beepInterval);
    }
  }, [stressLevel.effects.alertBeeps]);

  // Classes CSS pour les effets visuels
  const getStressClasses = useCallback(() => {
    const classes: string[] = [];
    
    if (stressLevel.effects.cursorGhost) {
      classes.push('cursor-ghost');
    }
    
    if (stressLevel.effects.glitchIcons) {
      classes.push('glitch-icons');
    }
    
    if (stressLevel.effects.windowDistortion) {
      classes.push('window-distortion');
    }
    
    if (stressLevel.effects.chromaticShift) {
      classes.push('chromatic-shift');
    }
    
    if (inversionActive) {
      classes.push('text-inversion');
    }
    
    if (freezeActive) {
      classes.push('system-freeze');
    }
    
    return classes.join(' ');
  }, [stressLevel.effects, inversionActive, freezeActive]);

  // Style pour le délai d'input
  const getInputStyle = useCallback(() => {
    if (stressLevel.effects.inputLag > 0) {
      return {
        transition: `all ${stressLevel.effects.inputLag}ms ease-out`,
        pointerEvents: freezeActive ? 'none' as const : 'auto' as const,
      };
    }
    return {};
  }, [stressLevel.effects.inputLag, freezeActive]);

  return {
    stressLevel,
    freezeActive,
    inversionActive,
    getStressClasses,
    getInputStyle,
  };
};